import {PickList as PrimePickList, PickListProps as PrimePickListProps} from "primereact/picklist";
import React, {useEffect} from "react";
import './PickList.scss';

interface PickListProps<T> extends PrimePickListProps {
    source: T[];
    target: T[];
    value?: T[];
    dataKey: string;
    itemTemplate: (item: T) => JSX.Element;
    onChange: (event: { source: T[], target: T[] }) => void;
    compare: (a: T, b: T) => boolean;
}

const normalizeList = <T,>(value: T[] | unknown): T[] => (Array.isArray(value) ? value : []);

const PickList = <T, >({
                           source,
                           target,
                           dataKey,
                           itemTemplate,
                           onChange,
                           compare,
                           value: controllerValue,
                           className,
                           ...props
                       }: PickListProps<T>) => {
    const resolvedTarget = normalizeList<T>(target ?? controllerValue);
    const [localSource, setLocalSource] = React.useState<T[]>(source || []);
    const [localTarget, setLocalTarget] = React.useState<T[]>(resolvedTarget);

    const updateSequence = (list: T[]) => {
        list.forEach((item, index) => {
            // @ts-ignore
            item.sequence = index + 1;
        });
    };

    useEffect(() => {
        const nextTarget = normalizeList<T>(target ?? controllerValue);
        const nextSource = source || [];
        const filteredSource = nextSource.filter(
            (item) => !nextTarget.some((selected) => compare(item, selected))
        );
        setLocalTarget(nextTarget);
        setLocalSource(filteredSource);
        updateSequence(nextTarget);
    // compare is intentionally omitted — callers often pass an inline function
    }, [source, target, controllerValue]);

    const findItemIndex = <T, >(list: T[], item: T, compare: (a: T, b: T) => boolean) => {
        return list.findIndex(listItem => compare(listItem, item));
    };

    const handleChange = (event: { source: T[], target: T[] }) => {
        updateSequence(event.target);
        setLocalSource(event.source);
        setLocalTarget(event.target);
        onChange(event);
    };

    const handleMoveAllToTarget = () => {
        const newTarget = [...localTarget, ...localSource];
        setLocalSource([]);
        setLocalTarget(newTarget);
        handleChange({source: [], target: newTarget});
    };

    const handleMoveAllToSource = () => {
        const newSource = [...localSource, ...localTarget];
        setLocalSource(newSource);
        setLocalTarget([]);
        handleChange({source: newSource, target: []});
    };


    const handleMoveToTarget = (event: any) => {
        const newSource = [...localSource];
        const newTarget = [...localTarget];

        event.value.forEach((item: T) => {
            const sourceIndex = findItemIndex(newSource, item, compare);
            if (sourceIndex !== -1) {
                newTarget.push(newSource[sourceIndex]);
                newSource.splice(sourceIndex, 1);
            }
        });

        setLocalSource(newSource);
        setLocalTarget(newTarget);
        handleChange({source: newSource, target: newTarget});
    };

    const handleMoveToSource = (event: any) => {
        const newSource = [...localSource];
        const newTarget = [...localTarget];

        event.value.forEach((item: T) => {
            const targetIndex = findItemIndex(newTarget, item, compare);
            if (targetIndex !== -1) {
                newSource.push(newTarget[targetIndex]);
                newTarget.splice(targetIndex, 1);
            }
        });

        setLocalSource(newSource);
        setLocalTarget(newTarget);
        handleChange({source: newSource, target: newTarget});
    };

    return (
        <PrimePickList
            source={localSource}
            target={localTarget}
            onChange={handleChange}
            onMoveToTarget={handleMoveToTarget}
            onMoveAllToSource={handleMoveAllToSource}
            onMoveAllToTarget={handleMoveAllToTarget}
            onMoveToSource={handleMoveToSource}
            itemTemplate={itemTemplate}
            dataKey={dataKey}
            className={["dashly-picklist", className].filter(Boolean).join(" ")}
            {...props}
        />
    );
};

export default PickList;